import React from 'react';

import { Box, Button } from '@mui/material';
import { ThreeBody } from '@uiball/loaders';

import { InfoBlock } from 'src/components/shared';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useDisableUserMfa, useUserMfaStatus } from 'src/hooks/auth';
import { useModalV2 } from 'src/hooks/shared';
import { useUser } from 'src/hooks/user';
import { EntityActionConfirmationModal } from 'src/modals/common';

type MfaManagementModalContentProps = {
  userId: string;
  onCloseModal: () => void;
};

export const MfaManagementModalContent: React.FC<MfaManagementModalContentProps> = ({
  userId,
  onCloseModal,
}) => {
  const { createApolloContext } = useNotification();
  const { openModal } = useModalV2();

  const { user, loading: userLoading } = useUser(userId);
  const { isMfaEnabled, loading: mfaStatusLoading } = useUserMfaStatus({ userId });

  const { disableUserMfa } = useDisableUserMfa({
    context: createApolloContext({
      successMessage: 'MFA Disabled Successfully for user',
    }),
  });

  const handleMfaDisable = React.useCallback(() => {
    openModal(EntityActionConfirmationModal, {
      id: ModalId.EntityActionConfirmation,
      onSubmit: async () => {
        await disableUserMfa(userId);
      },
      action: 'disable',
      entity: 'MFA for user',
      actionButtonColor: 'error',
    });
  }, [disableUserMfa, openModal, userId]);

  if (userLoading || mfaStatusLoading) {
    return (
      <Box display="flex" justifyContent="center">
        <ThreeBody size={100} color="#D9D9D9" />
      </Box>
    );
  }

  return (
    <React.Fragment>
      <InfoBlock.Container>
        <InfoBlock.Item title="Full Name" value={user?.fullName} />
        <InfoBlock.Item title="Email" value={user?.email} />
        <InfoBlock.Item title="MFA Status" value={isMfaEnabled ? 'Enabled' : 'Disabled'} />
      </InfoBlock.Container>

      <Box display="flex" justifyContent="center" gap={2} mt={2}>
        {!!isMfaEnabled && (
          <Button variant="outlined" color="info" onClick={handleMfaDisable}>
            Disable MFA
          </Button>
        )}

        <Button variant="outlined" color="error" onClick={onCloseModal}>
          Cancel
        </Button>
      </Box>
    </React.Fragment>
  );
};
